import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, BookOpen, Search, ChevronDown, ChevronUp, X, Sparkles, MapPin, GitBranch, Share2, Copy, ThumbsUp, ThumbsDown, AlertTriangle } from "lucide-react";
import { ProposeEdit, OriginalTranscript } from "@/components/ProposeEdit";
import { InfoPopover } from "@/components/InfoPopover";
import { useCategories } from "@/context/CategoriesContext";
import { renderSnippet } from "@/lib/searchSnippet";
import { ANY_LOCATION, LOCATION_FILTER_HINT, locationOptions, matchesLocation } from "@/lib/locationFilter";
import { matchesScope } from "@/lib/scope";
import { ScopeChip, ScopeFilterToggle } from "@/components/ScopeChip";
import { ProcessPicker } from "@/components/ProcessPicker";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TRANSFORM_LABELS = {
  raw: "Alleen transcript", summary: "Samenvatting", bullets: "Bulletpoints",
  sop: "Werkinstructie (SOP)", article: "Kennisartikel",
};
const ALL = "__ALL__";
const MEMO_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatDay(iso) {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} · ${d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return iso; }
}
function deriveTitle(r) {
  if (r.auto_title && r.auto_title.trim()) return r.auto_title.trim();
  if (r.question && r.question.trim()) return r.question.trim();
  const words = ((r.display_text || r.transcript) || "").split(/\s+/).filter(Boolean).slice(0, 8);
  return words.join(" ") + (words.length === 8 ? "…" : "");
}

export default function BibliotheekPage() {
  const { colorOf, descriptionOf } = useCategories();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("memos"); // memos | vragen
  const [questions, setQuestions] = useState([]);
  const [qLoading, setQLoading] = useState(false);
  const [openQ, setOpenQ] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [votingBusy, setVotingBusy] = useState(false); // FIX#7 — disable both thumbs while a vote is in flight
  const [ansLoading, setAnsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [brainQ, setBrainQ] = useState("");
  const [brainResult, setBrainResult] = useState(null);
  const [brainLoading, setBrainLoading] = useState(false);
  const [category, setCategory] = useState(ALL);
  const [tagFilter, setTagFilter] = useState(null);
  const [locationFilter, setLocationFilter] = useState(null);
  const [onlyAll, setOnlyAll] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [viewMode, setViewMode] = useState({}); // id -> processed | raw
  const [thread, setThread] = useState({}); // id -> array of thread nodes
  const [openQuestionsCount, setOpenQuestionsCount] = useState(null);

  // B3.2 — server full-text search (scope=library) takes over from the client filter at 2+ chars.
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchTokenRef = useRef(0); // FIX#8 (B-1) — discard a stale in-flight response

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/library?limit=200");
        setItems(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(items.map((r) => r.category))).sort(),
    [items]
  );

  const searching = query.trim().length >= 2;

  useEffect(() => {
    const q = query.trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (q.length < 2) {
      searchTokenRef.current += 1;
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      const myToken = ++searchTokenRef.current;
      try {
        const params = { q, scope: "library", limit: 50 };
        if (category !== ALL) params.category = category;
        const { data } = await api.get("/search", { params });
        if (searchTokenRef.current !== myToken) return; // a newer search already superseded this one
        setSearchResults(data);
      } catch (e) {
        if (searchTokenRef.current !== myToken) return;
        setSearchResults([]);
        toast.error(formatApiError(e));
      } finally {
        if (searchTokenRef.current === myToken) setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [query, category]);

  // Search results carry only a slim shape (+ snippet) — rendered as a dedicated compact
  // list (FIX#6, B-1); clicking one hydrates the FULL memo via openMemoFromSource and
  // expands it in the normal (non-search) list, instead of enriching against only the
  // already-loaded page (which used to silently drop hits outside that page).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((r) => {
      if (category !== ALL && r.category !== category) return false;
      if (tagFilter && !(r.tags || []).includes(tagFilter)) return false;
      if (!matchesLocation(r, locationFilter)) return false;
      if (!matchesScope(r, onlyAll)) return false;
      if (!q) return true;
      const hay = ((r.display_text || "") + " " + (r.transcript || "") + " " + (r.question || "") + " " + (r.auto_title || "") + " " + (r.tags || []).join(" ") + " " + (r.locations || []).join(" ") + " " + (r.user_name || "")).toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, category, tagFilter, locationFilter, onlyAll]);
  const setItemScope = (id, patch) => setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const toggleTag = (t) => setTagFilter((c) => (c === t ? null : t));
  const toggleLoc = (l) => setLocationFilter((c) => (c === l ? null : l));
  const locationChoices = useMemo(() => locationOptions(items), [items]);

  const loadQuestions = async () => {
    setQLoading(true);
    try {
      const [{ data }, oc] = await Promise.all([
        api.get("/library/questions"),
        api.get("/library/open-questions-count").catch(() => ({ data: { count: null } })),
      ]);
      setQuestions(data);
      setOpenQuestionsCount(oc.data.count);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setQLoading(false);
    }
  };

  const loadThread = async (id) => {
    if (thread[id]) return;
    try {
      const { data } = await api.get(`/library/item/${id}/thread`);
      setThread((t) => ({ ...t, [id]: data }));
    } catch {
      setThread((t) => ({ ...t, [id]: [] }));
    }
  };

  const openAnswer = async (q) => {
    setOpenQ(q);
    setAnswer(null);
    setAnsLoading(true);
    try {
      const { data } = await api.get(`/library/answer?question=${encodeURIComponent(q.question)}`);
      setAnswer(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setOpenQ(null);
    } finally {
      setAnsLoading(false);
    }
  };

  const shareAnswer = async (q, ans) => {
    const text = `${q.question}\n\n${ans?.answer_text || ""}`.trim();
    if (navigator.share) {
      try {
        await navigator.share({ title: q.question, text });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user cancelled the share sheet — do nothing
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Gekopieerd.");
    } catch {
      toast.error("Kopiëren mislukt.");
    }
  };

  const voteAnswer = async (q, vote) => {
    if (votingBusy) return; // FIX#7 — no double-click race while a vote is in flight
    setVotingBusy(true);
    try {
      const { data } = await api.post("/library/answer/vote", {
        question: q.question, vote, generated_at: answer?.generated_at,
      });
      setAnswer((a) => (a ? { ...a, feedback: data } : a));
      if (data.my_vote === vote) toast.success(vote === "up" ? "Bedankt voor je feedback." : "Bedankt, we bekijken dit antwoord.");
      else toast("Stem ingetrokken.");
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(formatApiError(e));
        await openAnswer(q); // reload the answer so the displayed version + votes stay in sync
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setVotingBusy(false);
    }
  };

  const switchTab = (t) => {
    setTab(t);
    setOpenQ(null);
    if (t === "vragen" && questions.length === 0) loadQuestions();
  };

  const askBrain = async (e) => {
    e?.preventDefault?.();
    if (!brainQ.trim() || brainLoading) return;
    setBrainLoading(true);
    setBrainResult(null);
    try {
      const { data } = await api.post("/brain/ask", { question: brainQ.trim() });
      setBrainResult(data);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBrainLoading(false);
    }
  };

  const openMemoFromSource = async (id) => {
    // Clear all filters FIRST so the memo isn't hidden by an active search/category/tag/loc filter.
    setQuery("");
    setCategory(ALL);
    setTagFilter(null);
    setLocationFilter(null);
    setOnlyAll(false);
    setTab("memos");
    setOpenQ(null);
    // If the memo isn't on the loaded first page, fetch it by id (server re-checks visibility).
    if (!items.some((i) => i.id === id)) {
      try {
        const { data } = await api.get(`/library/item/${encodeURIComponent(id)}`);
        setItems((prev) => (prev.some((i) => i.id === data.id) ? prev : [data, ...prev]));
      } catch {
        toast.error("Deze memo is niet (meer) zichtbaar.");
        return;
      }
    }
    setExpandedId(id);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="bibliotheek-item-${id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  // Round N — ?memo=<id> (link uit een trefwoordmelding) opent die memo één keer.
  const [searchParams, setSearchParams] = useSearchParams();
  const memoParam = searchParams.get("memo");
  useEffect(() => {
    if (loading || !memoParam) return;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("memo"); return next; }, { replace: true });
    if (MEMO_ID_RE.test(memoParam)) openMemoFromSource(memoParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, memoParam]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="bibliotheek-page">
      <div className="flex items-center gap-2.5 mb-1">
        <BookOpen size={18} className="text-tw-yellow" strokeWidth={2.5} />
        <h1 className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight">Bibliotheek</h1>
      </div>
      <p className="text-tw-muted text-sm mb-6">Teamkennis om te lezen — gedeelde memo's van het TW1-team.</p>

      {/* R8 — Vraag het brein (hero) */}
      <div className="mb-6 border border-tw-yellow/30 bg-tw-yellow/5 p-4" data-testid="brain-hero">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase mb-2 flex items-center gap-1.5">
          <Sparkles size={12} strokeWidth={2.5} /> Vraag het brein
        </div>
        <form onSubmit={askBrain} className="flex gap-2">
          <Input
            data-testid="brain-input"
            value={brainQ}
            onChange={(e) => setBrainQ(e.target.value)}
            placeholder="Stel een vraag aan de teamkennis…"
            className="bg-tw-black border-tw text-white h-11 rounded-none focus:border-tw-yellow"
          />
          <button type="submit" data-testid="brain-ask-btn" disabled={brainLoading}
            className="tw-press px-4 h-11 bg-tw-yellow text-black font-bold uppercase tracking-[0.15em] text-[11px] disabled:opacity-40">
            {brainLoading ? <Loader2 size={14} className="animate-spin" /> : "Vraag"}
          </button>
        </form>
        {brainLoading && <p className="mt-3 font-mono text-xs text-tw-muted" data-testid="brain-loading">Het brein denkt na…</p>}
        {brainResult && !brainLoading && (
          <div className="mt-4" data-testid="brain-result">
            <p className={`text-sm leading-relaxed whitespace-pre-wrap ${brainResult.not_found ? "text-tw-muted" : "text-white"}`} data-testid="brain-answer">
              {brainResult.answer}
            </p>
            {brainResult.sources?.length > 0 && (
              <div className="mt-4" data-testid="brain-sources">
                <div className="font-mono text-[9px] tracking-[0.3em] text-tw-muted uppercase mb-2">Bronnen</div>
                <div className="flex flex-col gap-2">
                  {brainResult.sources.map((s) => (
                    <div key={s.id} data-testid={`brain-source-${s.id}`}
                      onClick={() => openMemoFromSource(s.id)}
                      role="button" tabIndex={0}
                      onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); openMemoFromSource(s.id); } }}
                      className="bg-tw-black border border-tw px-3 py-2 tw-press cursor-pointer hover:border-tw-yellow">
                      <div className="text-white text-sm font-medium">{s.auto_title || s.category}</div>
                      <div className="font-mono text-[10px] text-tw-muted mt-0.5">
                        {s.user_name}{s.functie ? ` (${s.functie})` : ""} · {s.category}
                      </div>
                      {s.auto_summary && <p className="text-tw-muted text-xs mt-1">{s.auto_summary}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tab switch: Memo's | Kennisvragen */}
      <div className="flex gap-1 mb-6 border-b border-tw">
        <button type="button" onClick={() => switchTab("memos")} data-testid="bibliotheek-tab-memos"
          className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px ${tab === "memos" ? "text-tw-yellow border-tw-yellow" : "text-tw-muted border-transparent hover:text-white"}`}>
          Memo's
        </button>
        <button type="button" onClick={() => switchTab("vragen")} data-testid="bibliotheek-tab-vragen"
          className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px ${tab === "vragen" ? "text-tw-yellow border-tw-yellow" : "text-tw-muted border-transparent hover:text-white"}`}>
          Kennisvragen
        </button>
      </div>

      {tab === "vragen" ? (
        <KennisvragenView
          qLoading={qLoading} questions={questions} openQ={openQ} answer={answer}
          ansLoading={ansLoading} onOpen={openAnswer} onBack={() => { setOpenQ(null); setAnswer(null); }}
          openQuestionsCount={openQuestionsCount} onShare={shareAnswer} onVote={voteAnswer} voteBusy={votingBusy}
        />
      ) : (
      <>
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 mb-2">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tw-muted" />
          <Input
            data-testid="bibliotheek-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek in de bibliotheek…"
            className="pl-9 bg-tw-surface border-tw text-white h-11 rounded-none focus:border-tw-yellow"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="bibliotheek-category-filter" className="bg-tw-surface border-tw text-white h-11 rounded-none focus:border-tw-yellow w-full sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
            <SelectItem value={ALL} className="rounded-none">Alle categorieën</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={locationFilter || ANY_LOCATION} onValueChange={(v) => setLocationFilter(v === ANY_LOCATION ? null : v)}>
          <SelectTrigger data-testid="bibliotheek-location-filter" className="bg-tw-surface border-tw text-white h-11 rounded-none focus:border-tw-yellow w-full sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
            <SelectItem value={ANY_LOCATION} className="rounded-none">Alle locaties</SelectItem>
            {locationChoices.map((loc) => (
              <SelectItem key={loc} value={loc} className="rounded-none">{loc}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <ScopeFilterToggle active={onlyAll} onToggle={() => setOnlyAll((v) => !v)} testId="bibliotheek-scope-filter" />
        <p className="text-[11px] text-tw-muted" data-testid="bibliotheek-location-hint">{LOCATION_FILTER_HINT}</p>
      </div>

      {(tagFilter || locationFilter) && (
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="bibliotheek-active-filters">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-tw-muted">Filter:</span>
          {locationFilter && (
            <button type="button" onClick={() => setLocationFilter(null)} data-testid="bibliotheek-clear-location" className="tw-press inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-black bg-tw-yellow">
              <MapPin size={11} strokeWidth={2.5} /> {locationFilter} <X size={11} strokeWidth={2.8} />
            </button>
          )}
          {tagFilter && (
            <button type="button" onClick={() => setTagFilter(null)} data-testid="bibliotheek-clear-tag" className="tw-press inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] tracking-[0.1em] text-black bg-tw-yellow">
              #{tagFilter} <X size={11} strokeWidth={2.8} />
            </button>
          )}
        </div>
      )}

      {searching && (
        <p className="mb-4 font-mono text-[11px] text-tw-muted uppercase tracking-[0.15em]" data-testid="bibliotheek-search-status">
          {searchLoading ? "Zoeken…" : `${(searchResults || []).length} resulta${(searchResults || []).length === 1 ? "at" : "ten"} voor "${query.trim()}"`}
        </p>
      )}

      {searching ? (
        searchLoading ? (
          <div className="flex items-center gap-2 text-tw-muted py-16 justify-center" data-testid="bibliotheek-loading">
            <Loader2 className="animate-spin" size={18} /> <span className="font-mono text-sm">Zoeken…</span>
          </div>
        ) : (searchResults || []).length === 0 ? (
          <div className="text-center py-16" data-testid="bibliotheek-empty">
            <BookOpen size={32} className="text-tw-muted mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-white font-medium">Geen resultaten voor &quot;{query.trim()}&quot;</p>
            <p className="text-tw-muted text-sm mt-2 max-w-sm mx-auto">Pas je zoekterm of categorie aan.</p>
          </div>
        ) : (
          <ul className="divide-y divide-tw" data-testid="bibliotheek-search-results">
            {searchResults.map((r) => (
              <li key={r.id} data-testid={`bibliotheek-search-result-${r.id}`} className="py-5 tw-fadeup">
                <button
                  type="button"
                  onClick={() => openMemoFromSource(r.id)}
                  className="w-full text-left tw-press"
                >
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="inline-block w-1.5 h-1.5" style={{ backgroundColor: colorOf(r.category) }} aria-hidden />
                    <span className="font-mono uppercase text-[11px] tracking-[0.24em] text-tw-muted">{r.category}</span>
                    <span className="font-mono text-[11px] text-tw-muted">·</span>
                    <span className="font-mono text-[11px] text-tw-muted">{formatDay(r.created_at)}</span>
                  </div>
                  <h2 className="font-display text-lg sm:text-xl font-bold text-white leading-snug tracking-tight">{r.title}</h2>
                  <p className="mt-1.5 text-tw-muted text-sm leading-relaxed" data-testid={`bibliotheek-snippet-${r.id}`}>
                    {renderSnippet(r.snippet)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : loading ? (
        <div className="flex items-center gap-2 text-tw-muted py-16 justify-center" data-testid="bibliotheek-loading">
          <Loader2 className="animate-spin" size={18} /> <span className="font-mono text-sm">Laden…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" data-testid="bibliotheek-empty">
          <BookOpen size={32} className="text-tw-muted mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-white font-medium">
            {items.length === 0 ? "Er is nog geen kennis opengesteld" : "Geen resultaten"}
          </p>
          <p className="text-tw-muted text-sm mt-2 max-w-sm mx-auto">
            {items.length === 0
              ? "Vraag de beheerder om categorieën zichtbaar te maken."
              : "Pas je zoekopdracht, categorie of filters aan."}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-tw" data-testid="bibliotheek-list">
          {filtered.map((r) => {
            const catColor = colorOf(r.category);
            const isOpen = expandedId === r.id;
            const mode = viewMode[r.id] || (r.processed_text ? "processed" : "raw");
            return (
              <li key={r.id} data-testid={`bibliotheek-item-${r.id}`} className="py-5 tw-fadeup">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="inline-block w-1.5 h-1.5" style={{ backgroundColor: catColor }} aria-hidden />
                  <span className="font-mono uppercase text-[11px] tracking-[0.24em] text-tw-muted">{r.category}</span>
                  <InfoPopover text={descriptionOf(r.category)} testId={`bibliotheek-category-info-${r.id}`} />
                  <span className="font-mono text-[11px] text-tw-muted">·</span>
                  <span className="font-mono text-[11px] text-tw-muted">{r.user_name}{r.functie ? ` (${r.functie})` : ""}</span>
                  {r.user_location && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted border border-tw px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`bibliotheek-speaker-location-${r.id}`} title="Locatie waar deze memo bij hoort">
                      <MapPin size={9} strokeWidth={2.5} /> hoort bij {r.user_location}
                    </span>
                  )}
                  <ScopeChip item={r} canEdit={!!r.can_edit_scope} onChange={(s) => setItemScope(r.id, s)} testId={`bibliotheek-scope-${r.id}`} />
                  <span className="font-mono text-[11px] text-tw-muted">·</span>
                  <span className="font-mono text-[11px] text-tw-muted">{formatDay(r.created_at)}</span>
                </div>

                <h2 className="font-display text-lg sm:text-xl font-bold text-white leading-snug tracking-tight">{deriveTitle(r)}</h2>

                {r.transcript_warning && (
                  <div
                    className="mt-1.5 inline-flex items-center gap-1.5 border border-tw-yellow bg-tw-yellow/10 px-2 py-1 font-mono text-[10px] tracking-[0.1em] text-tw-yellow"
                    data-testid={`bibliotheek-transcript-warning-${r.id}`}
                  >
                    <AlertTriangle size={12} strokeWidth={2.5} />
                    Let op: deze opname bevatte veel stilte — controleer het transcript.
                  </div>
                )}

                {((r.tags && r.tags.length > 0) || (r.locations && r.locations.length > 0)) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(r.locations || []).map((loc) => (
                      <button type="button" key={`loc-${loc}`} onClick={() => toggleLoc(loc)} data-testid={`bibliotheek-location-${r.id}-${loc}`}
                        className={`tw-press inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] border ${locationFilter === loc ? "text-black bg-tw-yellow border-tw-yellow" : "text-tw-yellow border-tw-yellow/40 bg-tw-yellow/5 hover:bg-tw-yellow/15"}`}>
                        <MapPin size={10} strokeWidth={2.5} /> {loc}
                      </button>
                    ))}
                    {(r.tags || []).map((t) => (
                      <button type="button" key={`tag-${t}`} onClick={() => toggleTag(t)} data-testid={`bibliotheek-tag-${r.id}-${t}`}
                        className={`tw-press inline-block px-2 py-0.5 font-mono text-[10px] tracking-[0.1em] border ${tagFilter === t ? "text-black bg-tw-yellow border-tw-yellow" : "text-tw-muted border-tw bg-tw-black hover:border-tw-yellow hover:text-tw-yellow"}`}>
                        #{t}
                      </button>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    const next = isOpen ? null : r.id;
                    setExpandedId(next);
                    if (next) loadThread(next);
                  }}
                  data-testid={`bibliotheek-toggle-${r.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-tw-muted hover:text-tw-yellow"
                >
                  {isOpen ? <>Inklappen <ChevronUp size={13} /></> : <>Lees memo <ChevronDown size={13} /></>}
                </button>

                {isOpen && (
                  <div className="mt-3 pl-3 border-l-2 border-tw-yellow" data-testid={`bibliotheek-detail-${r.id}`}>
                    {r.question && (
                      <div className="mb-3">
                        <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase">Kennisvraag</div>
                        <p className="mt-1 text-sm italic text-tw-muted leading-snug">&ldquo;{r.question}&rdquo;</p>
                      </div>
                    )}
                    {r.auto_summary && (
                      <div className="mb-3">
                        <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase">Samenvatting (AI)</div>
                        <p className="mt-1 text-sm text-white leading-relaxed">{r.auto_summary}</p>
                      </div>
                    )}
                    {r.can_process && (
                      <ProcessPicker memo={r} testId={`bibliotheek-process-${r.id}`} onDone={(d) => {
                        setItems((prev) => prev.map((x) => (x.id === r.id ? { ...x, processed_text: d.processed_text, transform_type: d.transform_type, processed_at: d.processed_at } : x)));
                        setViewMode((v) => ({ ...v, [r.id]: "processed" }));
                      }} />
                    )}
                    {r.processed_text && (
                      <div className="mb-3 flex gap-2">
                        <button type="button" onClick={() => setViewMode((v) => ({ ...v, [r.id]: "processed" }))} data-testid={`bibliotheek-view-processed-${r.id}`}
                          className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${mode === "processed" ? "text-black bg-tw-yellow border-tw-yellow" : "text-tw-muted border-tw"}`}>
                          {TRANSFORM_LABELS[r.transform_type] || "Verwerkt"}
                        </button>
                        <button type="button" onClick={() => setViewMode((v) => ({ ...v, [r.id]: "raw" }))}
                          className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${mode === "raw" ? "text-black bg-tw-yellow border-tw-yellow" : "text-tw-muted border-tw"}`}>
                          Transcript
                        </button>
                      </div>
                    )}
                    <p className="text-white text-sm leading-relaxed whitespace-pre-wrap" data-testid={`bibliotheek-body-${r.id}`}>
                      {mode === "processed" && r.processed_text ? r.processed_text : (r.display_text || r.transcript)}
                    </p>
                    {r.display_text && !(mode === "processed" && r.processed_text) && (
                      <OriginalTranscript transcript={r.transcript} id={r.id} />
                    )}
                    {(thread[r.id]?.length || 0) > 1 && (
                      <div className="mt-4 pt-3 border-t border-tw" data-testid={`bibliotheek-thread-${r.id}`}>
                        <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase mb-2 flex items-center gap-1.5">
                          <GitBranch size={11} strokeWidth={2.5} /> Gesprek
                        </div>
                        <ol className="space-y-2">
                          {thread[r.id].map((node) => (
                            <li key={node.id} data-testid={`bibliotheek-thread-node-${r.id}-${node.id}`}
                              className={`pl-3 border-l-2 ${node.current ? "border-tw-yellow" : "border-tw"}`}>
                              {node.question && <p className="text-xs italic text-tw-muted">&ldquo;{node.question}&rdquo;</p>}
                              <p className={`text-sm leading-snug whitespace-pre-wrap break-words ${node.current ? "text-white" : "text-tw-muted"}`}
                                data-testid={`bibliotheek-thread-text-${r.id}-${node.id}`}>
                                {node.text || ""}
                              </p>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    <ProposeEdit recordingId={r.id} currentText={r.display_text || r.transcript} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      </>
      )}
    </div>
  );
}

function KennisvragenView({ qLoading, questions, openQ, answer, ansLoading, onOpen, onBack, openQuestionsCount, onShare, onVote, voteBusy }) {
  if (openQ) {
    return (
      <div data-testid="bibliotheek-answer-page">
        <div className="flex items-center justify-between mb-4">
          <button type="button" onClick={onBack} data-testid="bibliotheek-answer-back"
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-tw-muted hover:text-tw-yellow">
            ← Terug naar vragen
          </button>
          {!ansLoading && (
            <button type="button" onClick={() => onShare(openQ, answer)} data-testid="bibliotheek-answer-share-btn"
              className="tw-press inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-tw-muted hover:text-tw-yellow">
              {typeof navigator !== "undefined" && navigator.share ? <Share2 size={12} strokeWidth={2.5} /> : <Copy size={12} strokeWidth={2.5} />}
              {typeof navigator !== "undefined" && navigator.share ? "Delen" : "Kopieer"}
            </button>
          )}
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-white leading-snug mb-1">{openQ.question}</h2>
        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-tw-muted mb-5">{openQ.category}</div>

        {ansLoading ? (
          <div className="flex items-center gap-2 text-tw-muted py-10" data-testid="bibliotheek-answer-loading">
            <Loader2 className="animate-spin" size={16} /> <span className="font-mono text-sm">Beste antwoord wordt samengesteld…</span>
          </div>
        ) : (
          <>
            {answer?.answer_text ? (
              <div className="mb-3 p-4 border-l-2 border-tw-yellow bg-tw-yellow/5" data-testid="bibliotheek-answer-text">
                <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase mb-2 flex items-center gap-1.5">
                  <Sparkles size={11} strokeWidth={2.5} /> Beste antwoord
                </div>
                <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{answer.answer_text}</p>
              </div>
            ) : (
              <div className="mb-6 p-4 border border-tw text-tw-muted text-sm" data-testid="bibliotheek-answer-fallback">
                Er kon nu geen samenvattend antwoord worden gemaakt — bekijk hieronder de bronmemo's.
              </div>
            )}

            {answer?.answer_text && (
              <div className="flex items-center gap-3 mb-6" data-testid="bibliotheek-answer-feedback">
                <button
                  type="button"
                  onClick={() => onVote(openQ, "up")}
                  disabled={voteBusy}
                  data-testid="bibliotheek-answer-vote-up"
                  title="Dit antwoord klopt"
                  className={`tw-press inline-flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[11px] disabled:opacity-50 disabled:cursor-not-allowed ${
                    answer.feedback?.my_vote === "up"
                      ? "text-black bg-tw-yellow border-tw-yellow"
                      : "text-tw-muted border-tw hover:border-tw-yellow hover:text-tw-yellow"
                  }`}
                >
                  <ThumbsUp size={13} strokeWidth={2.5} /> {answer.feedback?.up ?? 0}
                </button>
                <button
                  type="button"
                  onClick={() => onVote(openQ, "down")}
                  disabled={voteBusy}
                  data-testid="bibliotheek-answer-vote-down"
                  title="Dit antwoord mist iets"
                  className={`tw-press inline-flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[11px] disabled:opacity-50 disabled:cursor-not-allowed ${
                    answer.feedback?.my_vote === "down"
                      ? "text-black bg-tw-red border-tw-red"
                      : "text-tw-muted border-tw hover:border-tw-red hover:text-tw-red"
                  }`}
                >
                  <ThumbsDown size={13} strokeWidth={2.5} /> {answer.feedback?.down ?? 0}
                </button>
              </div>
            )}

            <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase mb-3">
              Bronnen ({answer?.sources?.length || 0})
            </div>
            <ul className="divide-y divide-tw" data-testid="bibliotheek-answer-sources">
              {(answer?.sources || []).map((s) => (
                <li key={s.id} className="py-4" data-testid={`bibliotheek-source-${s.id}`}>
                  <div className="font-mono text-[11px] text-tw-muted mb-1 flex flex-wrap items-center gap-2">
                    <span>{s.user_name}{s.functie ? ` (${s.functie})` : ""}</span>
                    {s.user_location && (
                      <span className="text-[10px] uppercase tracking-[0.15em] border border-tw px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`bibliotheek-source-location-${s.id}`} title="Locatie waar deze memo bij hoort">
                        <MapPin size={9} strokeWidth={2.5} /> hoort bij {s.user_location}
                      </span>
                    )}
                    {(s.locations || []).map((loc) => (
                      <span key={`srcloc-${loc}`} className="text-[10px] uppercase tracking-[0.15em] text-tw-yellow border border-tw-yellow/40 bg-tw-yellow/5 px-1.5 py-0.5" title="Genoemd in de memo">{loc}</span>
                    ))}
                  </div>
                  {s.auto_summary && <p className="text-white text-sm mb-1">{s.auto_summary}</p>}
                  <p className="text-tw-muted text-sm leading-relaxed whitespace-pre-wrap">{s.transcript}</p>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  const chip = typeof openQuestionsCount === "number" && openQuestionsCount > 0 && (
    <p className="mb-4 font-mono text-[11px] text-tw-muted uppercase tracking-[0.15em]" data-testid="bibliotheek-open-questions-chip">
      {openQuestionsCount} kennisvra{openQuestionsCount === 1 ? "ag" : "gen"} staa{openQuestionsCount === 1 ? "t" : "n"} nog open
    </p>
  );

  if (qLoading) {
    return (
      <div className="flex items-center gap-2 text-tw-muted py-16 justify-center" data-testid="bibliotheek-vragen-loading">
        <Loader2 className="animate-spin" size={18} /> <span className="font-mono text-sm">Laden…</span>
      </div>
    );
  }
  if (questions.length === 0) {
    return (
      <>
        {chip}
        <div className="text-center py-16" data-testid="bibliotheek-vragen-empty">
          <BookOpen size={32} className="text-tw-muted mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-white font-medium">Nog geen beantwoorde kennisvragen</p>
          <p className="text-tw-muted text-sm mt-2 max-w-sm mx-auto">Zodra er memo's op vragen worden ingesproken, verschijnen hier de antwoorden.</p>
        </div>
      </>
    );
  }
  return (
    <>
      {chip}
      <ul className="divide-y divide-tw" data-testid="bibliotheek-vragen-list">
        {questions.map((q) => (
          <li key={q.question}>
            <button type="button" onClick={() => onOpen(q)} data-testid={`bibliotheek-question-${q.question.slice(0, 20)}`}
              className="w-full text-left py-4 tw-press hover:bg-tw-surface/40 transition-colors">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-mono uppercase text-[10px] tracking-[0.24em] text-tw-muted">{q.category}</span>
                <span className="font-mono text-[10px] text-tw-yellow">· {q.answer_count} antwoord{q.answer_count === 1 ? "" : "en"}</span>
              </div>
              <p className="text-white font-display font-bold leading-snug">{q.question}</p>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
